import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from './ui';

interface Year {
  id: number;
  short_name: string;
  full_name: string;
}

interface YearFormData {
  short_name: string;
  full_name: string;
}

const YearList: React.FC = () => {
  const [years, setYears] = useState<Year[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [yearToDelete, setYearToDelete] = useState<number | null>(null);
  const [editingYear, setEditingYear] = useState<Year | null>(null);
  const [formData, setFormData] = useState<YearFormData>({
    short_name: '',
    full_name: ''
  });

  const fetchYears = async () => {
    const response = await fetch('/api/years');
    const data = await response.json();
    setYears(data);
  };

  useEffect(() => {
    fetchYears();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingYear 
        ? `/api/years/${editingYear.id}`
        : '/api/years';
      
      const response = await fetch(url, {
        method: editingYear ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchYears();
        handleCloseForm();
      }
    } catch (error) {
      console.error('Error saving year:', error);
    }
  };

  const handleEdit = (year: Year) => {
    setEditingYear(year);
    setFormData({
      short_name: year.short_name,
      full_name: year.full_name
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    setYearToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!yearToDelete) return;
    
    try {
      const response = await fetch(`/api/years/${yearToDelete}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchYears();
      }
    } catch (error) {
      console.error('Error deleting year:', error);
    } finally {
      setIsDeleteDialogOpen(false);
      setYearToDelete(null);
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingYear(null);
    setFormData({ short_name: '', full_name: '' });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>年度管理</CardTitle>
          <Button
            onClick={() => setIsFormOpen(true)}
            className="bg-blue-500 hover:bg-blue-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            新增年度
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">简写</th>
                  <th className="p-2 text-left">年度名称</th>
                  <th className="p-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {years.map((year) => (
                  <tr key={year.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{year.short_name}</td>
                    <td className="p-2">{year.full_name}</td>
                    <td className="p-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(year)}
                        className="mr-2"
                      >
                        编辑
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(year.id)}
                      >
                        删除
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isFormOpen} onOpenChange={handleCloseForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingYear ? '编辑年度' : '新增年度'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">简写</label>
              <Input
                name="short_name"
                value={formData.short_name}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">年度名称</label>
              <Input
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseForm}
              >
                取消
              </Button>
              <Button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>确定要删除这个年度吗？此操作无法撤销。</p>
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              删除
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default YearList;